import { FormProps } from '@deltek/specification-client/core/Models/formProps'
import { Partnership } from '@deltek/specification-client/core/Models/product'
import { ListingDetailPartnershipLinkInput } from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    PartnerName,
    PartnerType,
} from '@deltek/specification-client/core/Widgets/ProductListing/Details/Partnerships/partnershipTypes'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import React from 'react'
import { EditPartnershipType } from './EditPartnershipType'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        maxWidth: 504,
        paddingBottom: theme.spacing(1),
    },
    content: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
    },
    fullWidth: {
        width: '100%',
    },
    title: {
        color: '#04325B',
        fontWeight: 500,
        borderBottom: '6px solid #04325B',
        textTransform: 'uppercase',
    },
}))

export interface NormalizedListingDetailPartnershipLinkInput
    extends Omit<Partial<ListingDetailPartnershipLinkInput>, 'url'> {
    url: FormProps
}

const getPastLinkDetail = (
    partnerType: PartnerType,
    name: PartnerName,
    links: Partial<Partnership>[]
) => {
    const link = links?.filter(
        (each) => each.partnerType === partnerType && each.name === name
    )?.[0]
    return {
        level: link?.level || '',
        partnerType,
        name,
        url: { value: link?.url || '', required: false, valid: true },
    } as NormalizedListingDetailPartnershipLinkInput
}

const getLinkDetail = (
    partnerType: PartnerType,
    name: PartnerName,
    links: NormalizedListingDetailPartnershipLinkInput[]
) => {
    return links?.filter(
        (each) => each.partnerType === partnerType && each.name === name
    )?.[0]
}

interface EditPartnershipsProps {
    pastPartnerships?: Partial<Partnership>[]
    handlePartnershipLinksChange: (
        links: Partial<ListingDetailPartnershipLinkInput>[],
        valid: boolean
    ) => void
    showSwatchbox?: boolean
}

export const EditPartnerships: React.FC<EditPartnershipsProps> = ({
    pastPartnerships,
    handlePartnershipLinksChange,
    showSwatchbox = false,
}) => {
    const classes = useStyles()

    const [localLinks, setLocalLinks] = React.useState<
        NormalizedListingDetailPartnershipLinkInput[]
    >([
        getPastLinkDetail('BIM', 'BIMsmith', pastPartnerships ?? []),
        getPastLinkDetail('Swatchbox', 'Swatchbox', pastPartnerships ?? []),
        getPastLinkDetail('Sustainability', 'ULE', pastPartnerships ?? []),
        getPastLinkDetail(
            'Sustainability',
            'Sustainable Minds',
            pastPartnerships ?? []
        ),
    ])

    const handleUpdateLink = (
        detail: NormalizedListingDetailPartnershipLinkInput
    ) => {
        const local = localLinks.map((each) => {
            return each.name === detail.name &&
                each.partnerType === detail.partnerType
                ? detail
                : each
        })

        setLocalLinks(local)

        handlePartnershipLinksChange(
            local.map((each) => ({ ...each, url: each.url.value ?? '' })),
            !local.some((each) => !each.url.valid)
        )
    }

    return (
        <GridContainer
            data-testid="dvPartnerships"
            className={classes.container}
        >
            <GridItem className={classes.fullWidth}>
                <Typography variant="h6" className={classes.title}>
                    Partnerships
                </Typography>
            </GridItem>
            <GridItem className={clsx(classes.content, classes.fullWidth)}>
                <GridContainer direction="column">
                    <EditPartnershipType
                        detail={getLinkDetail('BIM', 'BIMsmith', localLinks)}
                        handleUpdateLink={handleUpdateLink}
                    />
                    {showSwatchbox && (
                        <EditPartnershipType
                            detail={getLinkDetail(
                                'Swatchbox',
                                'Swatchbox',
                                localLinks
                            )}
                            handleUpdateLink={handleUpdateLink}
                        />
                    )}
                    <EditPartnershipType
                        detail={getLinkDetail(
                            'Sustainability',
                            'ULE',
                            localLinks
                        )}
                        handleUpdateLink={handleUpdateLink}
                    />
                    <EditPartnershipType
                        detail={getLinkDetail(
                            'Sustainability',
                            'Sustainable Minds',
                            localLinks
                        )}
                        handleUpdateLink={handleUpdateLink}
                    />
                </GridContainer>
            </GridItem>
        </GridContainer>
    )
}
